import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Star, X } from '@phosphor-icons/react';

export default function MemberReview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [completedBookings, setCompletedBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Récupérer les réservations terminées (DONE)
  useEffect(() => {
    const fetchCompletedBookings = async () => {
      setLoading(true);
      try {
        const response = await api.get('/bookings/my');
        // Filtrer les réservations avec statut DONE
        const doneBookings = response.data.filter(b => b.status === 'DONE');
        setCompletedBookings(doneBookings);
      } catch (err) {
        console.error('Erreur:', err);
        setError('Impossible de charger vos réservations');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchCompletedBookings();
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedBooking) {
      setError('Veuillez sélectionner une réservation');
      return;
    }
    if (rating === 0) {
      setError('Veuillez sélectionner une note');
      return;
    }
    if (!comment.trim() || comment.length < 10) {
      setError('Veuillez écrire un commentaire d\'au moins 10 caractères');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await api.post('/reviews', {
        bookingId: selectedBooking,
        rating: rating,
        comment: comment.trim()
      });
      setSuccess(true);
      setTimeout(() => {
        navigate('/espace-membre/dashboard');
      }, 2000);
    } catch (err) {
      console.error('Erreur:', err);
      if (err.response?.status === 409) {
        setError('Vous avez déjà laissé un avis pour cette réservation');
      } else {
        setError('Erreur lors de l\'envoi de votre avis. Veuillez réessayer.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    navigate('/connexion');
    return null;
  }

  return (
    <div className="pt-20 min-h-screen bg-desti-black">
      <section className="py-24 md:py-32">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs uppercase tracking-[0.2em] text-desti-gold mb-3">
              Donnez votre avis
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl text-white mb-8">
              Partager votre expérience
            </h1>
          </motion.div>

          {success ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-green-500/10 border border-green-500/30 rounded-lg p-8 text-center"
            >
              <div className="text-green-500 text-5xl mb-4">✓</div>
              <h2 className="text-white text-xl font-medium mb-2">Merci pour votre avis !</h2>
              <p className="text-desti-text">Votre témoignage sera publié après validation.</p>
              <p className="text-desti-text mt-4 text-sm">Redirection vers votre espace membre...</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Sélection de la réservation */}
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  Réservation concernée *
                </label>
                {loading ? (
                  <div className="text-desti-text">Chargement de vos réservations...</div>
                ) : completedBookings.length === 0 ? (
                  <div className="bg-desti-surface border border-white/10 rounded-lg p-6 text-center">
                    <p className="text-desti-text mb-4">
                      Vous n'avez pas encore de trajet terminé à évaluer.
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate('/reservation')}
                      className="text-desti-gold hover:text-desti-gold-hover transition-colors"
                    >
                      Réserver un trajet →
                    </button>
                  </div>
                ) : (
                  <select
                    value={selectedBooking}
                    onChange={(e) => setSelectedBooking(e.target.value)}
                    className="w-full bg-desti-surface border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-desti-gold transition-colors"
                    required
                  >
                    <option value="">Sélectionnez un trajet</option>
                    {completedBookings.map((booking) => (
                      <option key={booking.id} value={booking.id}>
                        {booking.pickupDate} - {booking.pickupAddress.substring(0, 40)}...
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Note par étoiles */}
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  Votre note *
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="focus:outline-none"
                    >
                      <Star
                        size={32}
                        weight="fill"
                        className={`transition-colors ${
                          (hoverRating || rating) >= star
                            ? 'text-desti-gold'
                            : 'text-white/20'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Commentaire */}
              <div>
                <label className="block text-white text-sm font-medium mb-2">
                  Votre commentaire *
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={5}
                  className="w-full bg-desti-surface border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-desti-gold transition-colors resize-none"
                  placeholder="Partagez votre expérience avec nous..."
                  required
                />
                <p className="text-xs text-desti-text mt-1">
                  Minimum 10 caractères
                </p>
              </div>

              {/* Message d'erreur */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              {/* Bouton de soumission */}
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => navigate('/espace-membre/dashboard')}
                  className="flex-1 border border-white/20 text-white font-medium py-3 hover:bg-white/5 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={submitting || completedBookings.length === 0}
                  className="flex-1 bg-desti-gold text-desti-black font-medium py-3 hover:bg-desti-gold-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Envoi en cours...' : 'Publier mon avis'}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}